import { useId, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { CATALOG, ORIGINS, ORIGIN_LABEL, httpStatusOf, isOrigin } from '@/domain';
import {
  percentChange,
  useReportSummary,
  useSalesReport,
} from '@/application/usecases/useArchive';
import type { ReportRange, SalesRollupRow } from '@/application/ports/ReportRepository';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { BarList } from '@/ui/data/BarList';
import { SkeletonCards } from '@/ui/feedback/Loading';
import { useMoney } from '@/ui/money/moneyView';
import { archiveOfPeriod } from './periodLinks';
import styles from './ReportsScreen.module.css';

// All time first, and chosen when the screen opens: the lab's request of 17 October 2026, item 4.
const RANGES: ReadonlyArray<{ key: ReportRange; label: string }> = [
  { key: 'all', label: strings.reports.rangeAll },
  { key: 'today', label: strings.reports.rangeToday },
  { key: 'week', label: strings.reports.rangeWeek },
  { key: 'month', label: strings.reports.rangeMonth },
];

/**
 * Reports. Section 5.2.
 *
 * Units sold and the best selling item come first and largest, because those
 * are the two metrics the client named as most important. Everything else on
 * this screen is supporting detail, and it is laid out that way rather than
 * being an equal grid of charts.
 *
 * The bars are divs. Section 2 rules out a chart library, and it would be the
 * single largest dependency in the bundle for four simple visualisations.
 */
export function ReportsScreen() {
  const ids = { dentist: useId(), origin: useId() };
  const navigate = useNavigate();
  const views = useSession().data?.views ?? [];
  const money = useMoney();
  const [range, setRange] = useState<ReportRange>('all');
  const [dentist, setDentist] = useState('all');
  const [origin, setOrigin] = useState('all');

  const sales = useSalesReport({ range, dentist, origin });
  const summary = useReportSummary(range);

  const report = sales.data;
  const delta =
    report === undefined ? null : percentChange(report.unitsSold, report.previousUnitsSold);

  const dentists = [...new Set((report?.byDentist ?? []).map((row) => row.key))].sort();
  const totalValue = (report?.byItem ?? []).reduce((sum, row) => sum + row.value, 0);
  const totalOrders = (report?.byItem ?? []).reduce((sum, row) => sum + row.orders, 0);
  const units = report?.unitsSold ?? 0;

  const hint =
    range === 'all'
      ? strings.reports.hintAll
      : range === 'today'
        ? strings.reports.hintToday
        : range === 'week'
          ? strings.reports.hintWeek
          : strings.reports.hintMonth;

  /*
   * Every figure opens what it counts, for a reader who holds where it goes.
   * The sales are of the cases delivered in the period, which is the Archive
   * between its days; Active, Due and Overdue are Orders narrowed as they are
   * counted. The lab's request of 17 October 2026, item 1.
   */
  const toArchive = views.includes('archive') ? () => void navigate(archiveOfPeriod(range)) : undefined;
  const toOrders = (search: string) =>
    views.includes('orders') ? () => void navigate({ pathname: '/orders', search }) : undefined;

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.reports.label}</span>
        <h2>{strings.reports.title}</h2>
        <span className={styles.hint}>{hint}</span>
        <span className={styles.spacer} />
        <div className={styles.seg} role="group" aria-label={strings.reports.rangeLabel}>
          {RANGES.map((option) => (
            <button
              key={option.key}
              className={range === option.key ? styles.on : undefined}
              aria-pressed={range === option.key}
              onClick={() => setRange(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.dentist}>
            {strings.reports.dentist}
          </label>
          <select
            id={ids.dentist}
            className={styles.select}
            value={dentist}
            onChange={(event) => setDentist(event.target.value)}
          >
            <option value="all">{strings.reports.allDentists}</option>
            {dentists.map((name) => (
              <option key={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* In place of the country. Section 1 of the request of 24 September 2026. */}
        <div className={styles.field}>
          <label className={styles.l} htmlFor={ids.origin}>
            {strings.reports.origin}
          </label>
          <select
            id={ids.origin}
            className={styles.select}
            value={origin}
            onChange={(event) => setOrigin(event.target.value)}
          >
            <option value="all">{strings.reports.allOrigins}</option>
            {ORIGINS.map((key) => (
              <option key={key} value={key}>
                {ORIGIN_LABEL[key]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {sales.isError && (
        <p className={styles.hint}>
          {httpStatusOf(sales.error) === 403 ? strings.reports.noMoney : strings.errors.server}
        </p>
      )}
      {sales.isPending && <SkeletonCards cards={4} label={strings.common.loadingReports} />}

      {report !== undefined && (
        <>
          <div className={styles.kpis}>
            <Kpi
              label={strings.reports.unitsSold}
              value={units.toLocaleString()}
              onPress={toArchive}
              trend={
                delta === null
                  ? strings.reports.noPrevious
                  : `${delta > 0 ? '+' : ''}${delta}% ${strings.reports.versusPrevious}`
              }
              direction={delta === null ? 'flat' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}
            />
            <Kpi
              label={strings.reports.bestSeller}
              value={report.bestSeller ?? strings.reports.noData}
              text
              onPress={toArchive}
              trend={
                report.byItem[0] === undefined
                  ? ''
                  : `${report.byItem[0].units} ${strings.archive.units.toLowerCase()}`
              }
            />
            <Kpi label={strings.reports.ordersDelivered} value={totalOrders.toLocaleString()} onPress={toArchive} />
            <Kpi label={strings.reports.valueTotal} value={money(totalValue)} onPress={toArchive} />
            <Kpi
              label={strings.reports.itemsSold}
              value={String(report.byItem.length)}
              trend={`${strings.reports.ofCatalogue} ${CATALOG.length}`}
              onPress={toArchive}
            />
            <Kpi
              label={strings.reports.averageUnit}
              value={money(units === 0 ? 0 : Math.round(totalValue / units))}
              onPress={toArchive}
            />
          </div>

          {report.byItem.length === 0 ? (
            <div className={styles.empty}>
              <h3>{strings.reports.noSales}</h3>
              <p>{strings.reports.noSalesDetail}</p>
            </div>
          ) : (
            <div className={styles.grid}>
              <Card title={strings.reports.byItem} sub={strings.reports.byItemSub}>
                <BarList rows={toRows(report.byItem.slice(0, 10))} />
              </Card>
              <Card title={strings.reports.byCategory} sub={strings.reports.byCategorySub}>
                <BarList rows={toRows(report.byCategory)} tone="soft" />
              </Card>
              <Card title={strings.reports.byDentist} sub={strings.reports.byDentistSub}>
                <BarList rows={toRows(report.byDentist.slice(0, 8))} />
              </Card>
              <Card title={strings.reports.byOrigin} sub={strings.reports.byOriginSub}>
                <BarList rows={toRows(report.byOrigin, originLabel)} tone="ink" />
              </Card>
            </div>
          )}
        </>
      )}

      {summary.isPending && <SkeletonCards cards={6} label={strings.common.loadingReports} />}

      {summary.data !== undefined && (
        <>
          <div className={styles.section}>
            <span className={styles.lbl}>{strings.reports.production}</span>
            <h2>{strings.reports.productionSub}</h2>
          </div>

          <div className={styles.kpis}>
            <Kpi label={strings.reports.active} value={String(summary.data.active)} onPress={toOrders('?status=active')} />
            <Kpi label={strings.reports.due} value={String(summary.data.due)} onPress={toOrders('?due=today')} />
            <Kpi label={strings.reports.overdue} value={String(summary.data.overdue)} onPress={toOrders('?due=overdue')} />
            <Kpi label={strings.reports.onTime} value={`${summary.data.onTimePercent}%`} onPress={toArchive} />
            <Kpi
              label={strings.reports.turnaround}
              value={`${summary.data.averageTurnaroundDays}${strings.reports.days}`}
              onPress={toArchive}
            />
            <Kpi label={strings.reports.completed} value={String(summary.data.completed)} onPress={toArchive} />
          </div>
        </>
      )}
    </div>
  );
}

function toRows(rollup: SalesRollupRow[], label: (key: string) => string = (key) => key) {
  return rollup.map((row) => ({ label: label(row.key), value: row.units }));
}

/** The origin rollup is keyed by the origin's key; a key this build does not know reads as itself. */
function originLabel(key: string): string {
  return isOrigin(key) ? ORIGIN_LABEL[key] : key;
}

function Kpi({
  label,
  value,
  trend,
  direction = 'flat',
  text = false,
  onPress,
}: {
  label: string;
  value: string;
  trend?: string;
  direction?: 'up' | 'down' | 'flat';
  text?: boolean;
  /** Opens what the figure counts. Absent, the card is a figure to read. */
  onPress?: () => void;
}) {
  const content = (
    <>
      <div className={styles.l}>{label}</div>
      <div className={`${styles.v} ${text ? styles.text : ''}`}>{value}</div>
      <div
        className={`${styles.t} ${direction === 'up' ? styles.up : direction === 'down' ? styles.down : ''}`}
      >
        {trend ?? ''}
      </div>
    </>
  );
  if (onPress === undefined) return <div className={styles.kpi}>{content}</div>;
  return (
    <button type="button" className={`${styles.kpi} ${styles.kpiPress}`} onClick={onPress}>
      {content}
    </button>
  );
}

function Card({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  return (
    <div className={styles.card}>
      <h3>{title}</h3>
      <div className={styles.sub}>{sub}</div>
      {children}
    </div>
  );
}
